import type { ActivityMessage, JobProgress, NotificationItem } from '@hoopoe/shared';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useOptionalConfirm } from '../components/ui/confirm';

interface ActivityApi {
  jobs: JobProgress[];
  notifications: NotificationItem[];
  connected: boolean;
  /** Client clock, corrected by the server's clock (for "last update 3s ago" and stall detection). */
  now: number;
  cancel(id: string): Promise<void>;
  retry(id: string): Promise<void>;
  markRead(id: string): Promise<void>;
  markAllRead(): Promise<void>;
}

const ActivityContext = createContext<ActivityApi | null>(null);
export const useActivity = () => {
  const ctx = useContext(ActivityContext);
  if (!ctx) throw new Error('useActivity needs <ActivityProvider>');
  return ctx;
};

const PING_MS = 25_000;

/**
 * One WebSocket per tab to the user's ActivityHub: snapshot on connect, then live job and notification
 * events. Reconnects with backoff; the server snapshot repairs anything missed (PLAN §12.5).
 */
export function ActivityProvider({ children, url = '/api/activity/ws' }: { children: ReactNode; url?: string }) {
  const [jobs, setJobs] = useState<Map<string, JobProgress>>(new Map());
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [connected, setConnected] = useState(false);
  const [skew, setSkew] = useState(0);
  const [now, setNow] = useState(Date.now());
  const retryRef = useRef(0);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    fetch('/api/notifications')
      .then(r => (r.ok ? r.json() : { notifications: [] }))
      .then((d: { notifications: NotificationItem[] }) => setNotifications(d.notifications))
      .catch(() => {});
  }, []);

  useEffect(() => {
    let ws: WebSocket | null = null;
    let ping: ReturnType<typeof setInterval> | undefined;
    let reconnect: ReturnType<typeof setTimeout> | undefined;
    let stopped = false;
    const connect = () => {
      const proto = location.protocol === 'https:' ? 'wss' : 'ws';
      ws = new WebSocket(`${proto}://${location.host}${url}`);
      ws.onopen = () => {
        retryRef.current = 0;
        setConnected(true);
        ping = setInterval(() => ws?.readyState === WebSocket.OPEN && ws.send('ping'), PING_MS);
      };
      ws.onmessage = e => {
        const msg = JSON.parse(e.data as string) as ActivityMessage;
        if ('serverTime' in msg) setSkew(msg.serverTime - Date.now());
        if (msg.type === 'snapshot') setJobs(new Map(msg.jobs.map(j => [j.id, j])));
        else if (msg.type === 'job') setJobs(prev => new Map(prev).set(msg.job.id, msg.job));
        else if (msg.type === 'notification') {
          setNotifications(prev => [msg.notification, ...prev]);
          if (typeof Notification !== 'undefined' && Notification.permission === 'granted' && document.hidden)
            new Notification(msg.notification.title);
        }
      };
      ws.onclose = () => {
        clearInterval(ping);
        setConnected(false);
        if (stopped) return;
        const delay = Math.min(30_000, 500 * 2 ** retryRef.current++);
        reconnect = setTimeout(connect, delay);
      };
    };
    connect();
    return () => {
      stopped = true;
      clearInterval(ping);
      clearTimeout(reconnect);
      ws?.close();
    };
  }, [url]);

  const post = useCallback(async (path: string) => {
    await fetch(path, { method: 'POST' });
  }, []);
  const confirm = useOptionalConfirm();
  /** Upload jobs follow the upload cancel rules: once AI work was spent, the server asks for confirmation. */
  const cancel = useCallback(
    async (id: string) => {
      const res = await fetch(`/api/activity/${id}/cancel`, { method: 'POST' });
      if (res.status !== 409) return;
      const body = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
      if (body.error !== 'confirm_required') return;
      const ok = confirm
        ? await confirm({ title: 'Cancel this upload?', body: body.message, confirmLabel: 'Cancel upload', destructive: true })
        : window.confirm(body.message ?? 'Cancel?');
      if (ok) await fetch(`/api/activity/${id}/cancel?confirm=1`, { method: 'POST' });
    },
    [confirm],
  );

  const api = useMemo<ActivityApi>(
    () => ({
      jobs: [...jobs.values()].sort((a, b) => b.startedAt - a.startedAt),
      notifications,
      connected,
      now: now + skew,
      cancel,
      retry: id => post(`/api/activity/${id}/retry`),
      markRead: async id => {
        setNotifications(prev => prev.map(n => (n.id === id ? { ...n, readAt: Date.now() } : n)));
        await post(`/api/notifications/${id}/read`);
      },
      markAllRead: async () => {
        setNotifications(prev => prev.map(n => ({ ...n, readAt: n.readAt ?? Date.now() })));
        await post('/api/notifications/read-all');
      },
    }),
    [jobs, notifications, connected, now, skew, post, cancel],
  );
  return <ActivityContext.Provider value={api}>{children}</ActivityContext.Provider>;
}
